/**
 * Normalizes any thrown value into flat, loggable fields.
 *
 * Supabase / PostgREST / Edge Function errors are plain objects (not Error
 * instances) carrying `message`, `code`, `details`, `hint` and sometimes
 * `status`. Reading only `error.message` on an `instanceof Error` check throws
 * all of that away, which is why historical logs read "Unknown error".
 */
export interface NormalizedError {
  error_message: string;
  error_name: string | null;
  error_code: string | null;
  error_details: string | null;
  error_hint: string | null;
  error_status: number | null;
}

const str = (v: unknown, max = 200): string | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string") return v.slice(0, max) || null;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v).slice(0, max);
  } catch {
    return null;
  }
};

export function normalizeError(error: unknown): NormalizedError {
  const empty: NormalizedError = {
    error_message: "",
    error_name: null,
    error_code: null,
    error_details: null,
    error_hint: null,
    error_status: null,
  };

  if (error === null || error === undefined) {
    return { ...empty, error_message: "Empty error (null/undefined thrown)" };
  }

  if (typeof error === "string") {
    return { ...empty, error_message: error.slice(0, 200) };
  }

  if (typeof error === "object") {
    const e = error as Record<string, unknown>;
    const message =
      str(e.message) ??
      str(e.error_description) ??
      str(e.error) ??
      str(e.msg) ??
      // Last resort: keep a JSON snippet so we never log "Unknown error"
      str(error, 300) ??
      "Unserializable error object";

    const statusRaw = e.status ?? e.statusCode ?? e.httpStatus;

    return {
      error_message: message,
      error_name:
        str(e.name) ?? (error instanceof Error ? error.name : null) ?? null,
      error_code: str(e.code, 60),
      error_details: str(e.details, 300),
      error_hint: str(e.hint, 200),
      error_status: typeof statusRaw === "number" ? statusRaw : null,
    };
  }

  return { ...empty, error_message: String(error).slice(0, 200) };
}

/** Short one-line summary suitable for the log description column. */
export function describeError(n: NormalizedError): string {
  const parts = [n.error_message];
  if (n.error_code) parts.push(`[${n.error_code}]`);
  if (n.error_status) parts.push(`(HTTP ${n.error_status})`);
  return parts.join(" ");
}
